import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Building2, FileText, Users, IdCard, FolderOpen, ShieldCheck,
  Truck, Fingerprint, CreditCard, ClipboardCheck, Sparkles,
  ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Loader2,
  Upload, X, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  welcome: Sparkles, company: Building2, tax: FileText, owners: Users,
  identity: IdCard, documents: FolderOpen, aml: ShieldCheck,
  shipper: Truck, fraud: Fingerprint, billing: CreditCard, review: ClipboardCheck,
};

const cardCls = "bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6";
const inputCls = "w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm";
const labelCls = "text-sm font-medium block mb-1.5";

// ---------- shared helpers ----------
const Field = ({ label, children, required, hint }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-rose-500 ml-1">*</span>}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const AddressBlock = ({ value, onChange, prefix = "addr" }) => {
  const v = value || {};
  const upd = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <input placeholder="Street address" className={inputCls} value={v.street || ''} onChange={e => upd('street', e.target.value)} data-testid={`${prefix}-street`} />
      </div>
      <input placeholder="City" className={inputCls} value={v.city || ''} onChange={e => upd('city', e.target.value)} data-testid={`${prefix}-city`} />
      <input placeholder="State / Region" className={inputCls} value={v.state || ''} onChange={e => upd('state', e.target.value)} data-testid={`${prefix}-state`} />
      <input placeholder="ZIP / Postal" className={inputCls} value={v.zip || ''} onChange={e => upd('zip', e.target.value)} data-testid={`${prefix}-zip`} />
      <input placeholder="Country" className={inputCls} value={v.country || 'US'} onChange={e => upd('country', e.target.value)} data-testid={`${prefix}-country`} />
    </div>
  );
};

const FileInput = ({ label, onFile, testid }) => {
  const [name, setName] = useState('');
  return (
    <label className="flex items-center gap-3 p-4 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-400 cursor-pointer transition-all">
      <Upload className="w-5 h-5 text-slate-400" />
      <div className="flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-slate-400">{name || 'PNG, JPG, or PDF up to 8MB'}</p>
      </div>
      <input type="file" className="hidden" accept="image/*,application/pdf" data-testid={testid}
        onChange={e => {
          const f = e.target.files?.[0]; if (!f) return;
          setName(f.name);
          const reader = new FileReader();
          reader.onload = () => onFile({ filename: f.name, content_type: f.type, content_base64: reader.result });
          reader.readAsDataURL(f);
        }}
      />
    </label>
  );
};

// ---------- page ----------
export default function OnboardingWizardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  // form state
  const [company, setCompany] = useState({ entity_type: 'multi_member_llc', country_of_formation: 'US', business_address: { country: 'US' } });
  const [tax, setTax] = useState({ form_type: 'w9', tin_type: 'ein', country: 'US', certification_accepted: false });
  const [owners, setOwners] = useState({ owners: [], no_owners_25_plus: false, control_person_full_name: '' });
  const [identity, setIdentity] = useState({ id_type: 'drivers_license' });
  const [docs, setDocs] = useState([]);
  const [aml, setAml] = useState({});
  const [shipper, setShipper] = useState({ ships_internationally: false, ships_restricted_items: false, primary_product_categories: [], primary_destinations: ['US'], origin_countries: ['US'] });
  const [fraudResult, setFraudResult] = useState(null);
  const [review, setReview] = useState({});
  const [billing, setBilling] = useState({ billing_method: 'credit_card', acknowledge_billing_terms: false, shipping_protection_tier: 'standard', payment_protection_tier: 'standard', alternative_protection_tier: 'basic', return_protection_tier: 'basic' });

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          fetch(`${API}/api/onboarding/kys/config`).then(r => r.json()),
          fetch(`${API}/api/onboarding/kys/status`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        setCfg(c);
        setStatus(s);
        if (s?.current_step && s.current_step > 1) setStep(Math.min(s.current_step, 11));
      } catch (e) { console.error(e); }
    })();
  }, [token]);

  const post = async (path, body) => {
    setSaving(true); setBanner(null);
    try {
      const res = await fetch(`${API}/api/onboarding/kys/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setBanner({ type: 'error', text: data.detail || 'Save failed' }); return null; }
      return data;
    } catch (e) { setBanner({ type: 'error', text: String(e) }); return null; }
    finally { setSaving(false); }
  };

  const steps = cfg?.steps || [];
  const current = steps.find(s => s.num === step);
  const Icon = ICONS[current?.id] || Info;
  const progress = useMemo(() => Math.round(((step - 1) / 11) * 100), [step]);

  if (!cfg) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
    </div>
  );

  const next = () => setStep(s => Math.min(11, s + 1));
  const back = () => setStep(s => Math.max(1, s - 1));

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="kys-wizard">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg">SelleSend Verification</h1>
              <p className="text-xs text-slate-500">KYS / KYB / AML — FinCEN CDD compliant onboarding</p>
            </div>
          </div>
          <div className="text-xs text-slate-500">Step {step} of 11 · {progress}% complete</div>
        </div>
        <div className="h-1 bg-slate-100 dark:bg-slate-800">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Left rail */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 space-y-1.5" data-testid="wizard-rail">
            {steps.map(s => {
              const I = ICONS[s.id] || Info;
              const done = (status?.steps_completed || []).includes(s.id);
              const active = s.num === step;
              return (
                <button key={s.num} onClick={() => setStep(s.num)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    active ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                    : done ? 'text-emerald-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                  data-testid={`rail-${s.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? 'bg-emerald-500 text-white' : done ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : <I className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">Step {s.num}</p>
                    <p className="text-sm truncate">{s.label}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main content */}
        <main className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Icon className="w-6 h-6 text-emerald-600 dark:text-emerald-300" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Step {step} of 11</p>
              <h2 className="text-2xl font-semibold" data-testid="step-title">{current?.label}</h2>
            </div>
          </div>

          {banner && (
            <div className={`p-4 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'error' ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'}`} data-testid="wizard-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>{banner.text}</span>
            </div>
          )}

          {step === 1 && (
            <div className={cardCls} data-testid="step-welcome">
              <h3 className="text-lg font-semibold mb-2">Welcome to SelleSend Verification</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                To ship, insure, and process payments through SelleSend we&apos;re required to verify your business under
                the FinCEN Customer Due Diligence rule (31 CFR § 1010.230), OFAC sanctions rules, and our
                Anti-Money-Laundering program.
              </p>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> Company info + tax form (W-9 or W-8BEN-E)</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> Beneficial owners (25%+) + a control person</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> Officer identity verification (ID + selfie)</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> Corporate documents upload</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> AML & OFAC attestation</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> SelleSend fraud analytics scan (multi-signal)</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> Pricing, protection tiers, billing terms</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> Legal review & signature — logged with IP, timestamp, and version</li>
              </ul>
              <p className="text-xs text-slate-500 mb-4">Time to complete: about 8–12 minutes. You can leave and resume anytime.</p>
              <Button onClick={next} className="w-full h-12" data-testid="welcome-continue">Begin verification <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          )}

          {step === 2 && (
            <div className={cardCls + " space-y-5"} data-testid="step-company">
              <Field label="Legal business name" required>
                <input className={inputCls} value={company.legal_name || ''} onChange={e => setCompany({ ...company, legal_name: e.target.value })} data-testid="co-legal-name" />
              </Field>
              <Field label="DBA (doing business as)">
                <input className={inputCls} value={company.dba_name || ''} onChange={e => setCompany({ ...company, dba_name: e.target.value })} data-testid="co-dba" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Entity type" required>
                  <select className={inputCls} value={company.entity_type} onChange={e => setCompany({ ...company, entity_type: e.target.value })} data-testid="co-entity">
                    {cfg.entity_types.map(t => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
                  </select>
                </Field>
                <Field label="EIN (XX-XXXXXXX)" required>
                  <input className={inputCls} value={company.ein || ''} onChange={e => setCompany({ ...company, ein: e.target.value })} placeholder="12-3456789" data-testid="co-ein" />
                </Field>
                <Field label="Date of formation" required>
                  <input type="date" className={inputCls} value={company.date_of_formation || ''} onChange={e => setCompany({ ...company, date_of_formation: e.target.value })} data-testid="co-formation-date" />
                </Field>
                <Field label="State of formation" required>
                  <input className={inputCls} value={company.state_of_formation || ''} onChange={e => setCompany({ ...company, state_of_formation: e.target.value })} placeholder="DE" data-testid="co-formation-state" />
                </Field>
                <Field label="Business phone" required>
                  <input className={inputCls} value={company.business_phone || ''} onChange={e => setCompany({ ...company, business_phone: e.target.value })} data-testid="co-phone" />
                </Field>
                <Field label="Business email" required>
                  <input type="email" className={inputCls} value={company.business_email || ''} onChange={e => setCompany({ ...company, business_email: e.target.value })} data-testid="co-email" />
                </Field>
                <Field label="Website">
                  <input className={inputCls} value={company.website || ''} onChange={e => setCompany({ ...company, website: e.target.value })} placeholder="https://" data-testid="co-website" />
                </Field>
                <Field label="NAICS code">
                  <input className={inputCls} value={company.naics_code || ''} onChange={e => setCompany({ ...company, naics_code: e.target.value })} placeholder="454110" data-testid="co-naics" />
                </Field>
              </div>
              <Field label="Business address" required>
                <AddressBlock value={company.business_address} onChange={a => setCompany({ ...company, business_address: a })} prefix="co-biz-addr" />
              </Field>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="co-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('company', company); if (r) next(); }} disabled={saving} data-testid="co-save">
                  {saving ? 'Saving…' : 'Save & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className={cardCls + " space-y-5"} data-testid="step-tax">
              <Field label="Which tax form applies?" required>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {cfg.tax_forms.map(f => (
                    <button key={f.id} type="button" onClick={() => setTax({ ...tax, form_type: f.id })}
                      className={`p-4 rounded-xl border-2 text-left ${tax.form_type === f.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                      data-testid={`tax-form-${f.id}`}>
                      <p className="font-semibold text-sm">{f.id.toUpperCase()}</p>
                      <p className="text-xs text-slate-500 mt-1">{f.label}</p>
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Taxpayer name" required>
                  <input className={inputCls} value={tax.taxpayer_name || ''} onChange={e => setTax({ ...tax, taxpayer_name: e.target.value })} data-testid="tax-name" />
                </Field>
                <Field label="TIN type" required>
                  <select className={inputCls} value={tax.tin_type} onChange={e => setTax({ ...tax, tin_type: e.target.value })} data-testid="tax-tin-type">
                    <option value="ssn">SSN</option><option value="ein">EIN</option><option value="foreign">Foreign TIN</option>
                  </select>
                </Field>
                <Field label="TIN (full)" required hint="Stored hashed — only last 4 kept in plaintext.">
                  <input className={inputCls} value={tax.tin || ''} onChange={e => setTax({ ...tax, tin: e.target.value })} data-testid="tax-tin" />
                </Field>
                <Field label="Federal tax classification">
                  <input className={inputCls} value={tax.federal_tax_classification || ''} onChange={e => setTax({ ...tax, federal_tax_classification: e.target.value })} placeholder="e.g. C-Corp" data-testid="tax-classification" />
                </Field>
              </div>
              <Field label="Address on file" required>
                <AddressBlock value={tax.address} onChange={a => setTax({ ...tax, address: a })} prefix="tax-addr" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Country" required>
                  <input className={inputCls} value={tax.country} onChange={e => setTax({ ...tax, country: e.target.value })} data-testid="tax-country" />
                </Field>
                {tax.form_type === 'w8bene' && (
                  <Field label="Treaty country">
                    <input className={inputCls} value={tax.treaty_country || ''} onChange={e => setTax({ ...tax, treaty_country: e.target.value })} data-testid="tax-treaty" />
                  </Field>
                )}
                <Field label="Signer full name" required>
                  <input className={inputCls} value={tax.signature_full_name || ''} onChange={e => setTax({ ...tax, signature_full_name: e.target.value })} data-testid="tax-sig-name" />
                </Field>
                <Field label="Signer title">
                  <input className={inputCls} value={tax.signature_title || ''} onChange={e => setTax({ ...tax, signature_title: e.target.value })} data-testid="tax-sig-title" />
                </Field>
              </div>
              <label className="flex items-start gap-2 text-sm" data-testid="tax-cert">
                <input type="checkbox" className="mt-1" checked={tax.certification_accepted} onChange={e => setTax({ ...tax, certification_accepted: e.target.checked })} />
                <span>Under penalties of perjury, I certify the information provided is true, correct, and complete.</span>
              </label>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="tax-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('tax', tax); if (r) next(); }} disabled={saving} data-testid="tax-save">
                  {saving ? 'Saving…' : 'Save & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className={cardCls + " space-y-5"} data-testid="step-owners">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg text-sm flex gap-2">
                <Info className="w-4 h-4 mt-0.5" />
                FinCEN CDD Rule 31 CFR § 1010.230 requires listing every individual who owns 25% or more of the entity, plus one control person.
              </div>
              <Field label="Control person (required)" required hint="Any senior officer who exercises significant control (CEO, CFO, COO, General Counsel, Managing Member, etc.)">
                <input className={inputCls} value={owners.control_person_full_name} onChange={e => setOwners({ ...owners, control_person_full_name: e.target.value })} data-testid="owner-control-name" />
              </Field>
              <label className="flex items-center gap-2 text-sm" data-testid="owner-none-25">
                <input type="checkbox" checked={owners.no_owners_25_plus} onChange={e => setOwners({ ...owners, no_owners_25_plus: e.target.checked })} />
                No single individual owns 25% or more.
              </label>
              {!owners.no_owners_25_plus && (
                <div className="space-y-3">
                  {owners.owners.map((o, i) => (
                    <div key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3 relative" data-testid={`owner-row-${i}`}>
                      <button className="absolute top-2 right-2 text-slate-400 hover:text-rose-500" onClick={() => setOwners({ ...owners, owners: owners.owners.filter((_, j) => j !== i) })} data-testid={`owner-remove-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input className={inputCls} placeholder="Full name" value={o.full_name || ''} onChange={e => { const arr = [...owners.owners]; arr[i].full_name = e.target.value; setOwners({ ...owners, owners: arr }); }} data-testid={`owner-name-${i}`} />
                        <input className={inputCls} type="date" value={o.date_of_birth || ''} onChange={e => { const arr = [...owners.owners]; arr[i].date_of_birth = e.target.value; setOwners({ ...owners, owners: arr }); }} data-testid={`owner-dob-${i}`} />
                        <input className={inputCls} placeholder="SSN last 4" maxLength={4} value={o.ssn_last4 || ''} onChange={e => { const arr = [...owners.owners]; arr[i].ssn_last4 = e.target.value; setOwners({ ...owners, owners: arr }); }} data-testid={`owner-ssn4-${i}`} />
                        <input className={inputCls} type="number" min={0} max={100} placeholder="Ownership %" value={o.ownership_percent ?? ''} onChange={e => { const arr = [...owners.owners]; arr[i].ownership_percent = parseFloat(e.target.value); setOwners({ ...owners, owners: arr }); }} data-testid={`owner-pct-${i}`} />
                        <input className={inputCls} placeholder="Citizenship" value={o.citizenship || 'US'} onChange={e => { const arr = [...owners.owners]; arr[i].citizenship = e.target.value; setOwners({ ...owners, owners: arr }); }} data-testid={`owner-citz-${i}`} />
                      </div>
                      <AddressBlock value={o.residential_address} onChange={a => { const arr = [...owners.owners]; arr[i].residential_address = a; setOwners({ ...owners, owners: arr }); }} prefix={`owner-addr-${i}`} />
                      <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                        <input type="checkbox" checked={o.politically_exposed_person || false} onChange={e => { const arr = [...owners.owners]; arr[i].politically_exposed_person = e.target.checked; setOwners({ ...owners, owners: arr }); }} data-testid={`owner-pep-${i}`} />
                        Politically Exposed Person (PEP)
                      </label>
                    </div>
                  ))}
                  <Button variant="outline" onClick={() => setOwners({ ...owners, owners: [...owners.owners, { citizenship: 'US', residential_address: { country: 'US' } }] })} data-testid="owner-add">
                    + Add beneficial owner
                  </Button>
                </div>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="owner-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('owners', owners); if (r) { if (r.ofac_hits?.length) setBanner({ type: 'error', text: `OFAC hit on: ${r.ofac_hits.join(', ')} — cannot continue.` }); else next(); } }} disabled={saving} data-testid="owner-save">
                  {saving ? 'Saving…' : 'Screen & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className={cardCls + " space-y-5"} data-testid="step-identity">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full legal name" required>
                  <input className={inputCls} value={identity.full_name || ''} onChange={e => setIdentity({ ...identity, full_name: e.target.value })} data-testid="id-name" />
                </Field>
                <Field label="Date of birth" required>
                  <input type="date" className={inputCls} value={identity.date_of_birth || ''} onChange={e => setIdentity({ ...identity, date_of_birth: e.target.value })} data-testid="id-dob" />
                </Field>
                <Field label="SSN last 4" required>
                  <input maxLength={4} className={inputCls} value={identity.ssn_last4 || ''} onChange={e => setIdentity({ ...identity, ssn_last4: e.target.value })} data-testid="id-ssn4" />
                </Field>
                <Field label="ID type" required>
                  <select className={inputCls} value={identity.id_type} onChange={e => setIdentity({ ...identity, id_type: e.target.value })} data-testid="id-type">
                    <option value="drivers_license">Driver&apos;s license</option>
                    <option value="passport">Passport</option>
                    <option value="state_id">State ID</option>
                  </select>
                </Field>
                <Field label="ID number" required>
                  <input className={inputCls} value={identity.id_number || ''} onChange={e => setIdentity({ ...identity, id_number: e.target.value })} data-testid="id-number" />
                </Field>
                <Field label="Issuing state / country">
                  <input className={inputCls} value={identity.id_issuing_state || ''} onChange={e => setIdentity({ ...identity, id_issuing_state: e.target.value })} data-testid="id-state" />
                </Field>
                <Field label="Expiration date" required>
                  <input type="date" className={inputCls} value={identity.id_expiration || ''} onChange={e => setIdentity({ ...identity, id_expiration: e.target.value })} data-testid="id-exp" />
                </Field>
              </div>
              <Field label="Residential address" required>
                <AddressBlock value={identity.residential_address} onChange={a => setIdentity({ ...identity, residential_address: a })} prefix="id-res-addr" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FileInput label="ID front" testid="id-front" onFile={f => setIdentity({ ...identity, id_front_base64: f.content_base64 })} />
                <FileInput label="ID back" testid="id-back" onFile={f => setIdentity({ ...identity, id_back_base64: f.content_base64 })} />
                <FileInput label="Live selfie" testid="id-selfie" onFile={f => setIdentity({ ...identity, selfie_base64: f.content_base64 })} />
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="id-back-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('identity', identity); if (r) next(); }} disabled={saving} data-testid="id-save">
                  {saving ? 'Saving…' : 'Save & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className={cardCls + " space-y-4"} data-testid="step-docs">
              <p className="text-sm text-slate-600 dark:text-slate-400">Upload the corporate documents that match your entity. All uploads are encrypted at rest.</p>
              {[
                { id: 'ein_letter', label: 'EIN Assignment Letter (CP 575)' },
                { id: 'articles_of_incorporation', label: 'Articles of Incorporation / Organization' },
                { id: 'operating_agreement', label: 'Operating Agreement / Bylaws' },
                { id: 'proof_of_address', label: 'Proof of business address (utility bill, lease)' },
              ].map(d => (
                <FileInput key={d.id} label={d.label} testid={`doc-${d.id}`} onFile={f => setDocs(prev => [...prev.filter(x => x.doc_type !== d.id), { ...f, doc_type: d.id }])} />
              ))}
              {docs.length > 0 && (
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-sm text-emerald-700 dark:text-emerald-300" data-testid="doc-count">
                  {docs.length} document{docs.length > 1 ? 's' : ''} ready to upload
                </div>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="doc-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('documents', { documents: docs }); if (r) next(); }} disabled={saving || docs.length === 0} data-testid="doc-save">
                  {saving ? 'Uploading…' : 'Upload & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 7 && (
            <div className={cardCls + " space-y-4"} data-testid="step-aml">
              <p className="text-sm text-slate-600 dark:text-slate-400">Please affirm each statement below. These attestations satisfy our AML program and OFAC screening requirements.</p>
              {[
                ['understands_aml_program', 'I understand SelleSend maintains an AML program and reserves the right to file SARs and freeze funds.'],
                ['not_on_sanctions_list', 'Neither I nor the business appears on any OFAC/SDN, HMT, EU, UN, or other sanctions list.'],
                ['funds_from_legitimate_sources', 'All funds and shipped goods derive from legitimate lawful sources.'],
                ['will_report_suspicious_activity', 'I will promptly report any suspicious activity or requests to SelleSend Compliance.'],
                ['will_maintain_records', 'I will maintain shipping and financial records for at least five (5) years.'],
                ['authorized_to_sign', 'I am authorized to bind the business to these representations.'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-start gap-2 text-sm" data-testid={`aml-${key}`}>
                  <input type="checkbox" className="mt-1" checked={aml[key] || false} onChange={e => setAml({ ...aml, [key]: e.target.checked })} />
                  <span>{label}</span>
                </label>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <Field label="Signer name" required>
                  <input className={inputCls} value={aml.signer_name || ''} onChange={e => setAml({ ...aml, signer_name: e.target.value })} data-testid="aml-signer-name" />
                </Field>
                <Field label="Signer title" required>
                  <input className={inputCls} value={aml.signer_title || ''} onChange={e => setAml({ ...aml, signer_title: e.target.value })} data-testid="aml-signer-title" />
                </Field>
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="aml-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('aml', aml); if (r) next(); }} disabled={saving} data-testid="aml-save">
                  {saving ? 'Saving…' : 'Save & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 8 && (
            <div className={cardCls + " space-y-5"} data-testid="step-shipper">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Monthly shipment volume" required>
                  <select className={inputCls} value={shipper.monthly_shipment_volume || '1-50'} onChange={e => setShipper({ ...shipper, monthly_shipment_volume: e.target.value })} data-testid="ship-volume">
                    <option value="1-50">1–50</option>
                    <option value="51-200">51–200</option>
                    <option value="201-1000">201–1,000</option>
                    <option value="1000+">1,000+</option>
                  </select>
                </Field>
                <Field label="Average shipment value ($)" required>
                  <input type="number" className={inputCls} value={shipper.average_shipment_value ?? ''} onChange={e => setShipper({ ...shipper, average_shipment_value: parseFloat(e.target.value) })} data-testid="ship-avg-value" />
                </Field>
                <Field label="Max shipment value ($)" required>
                  <input type="number" className={inputCls} value={shipper.max_shipment_value ?? ''} onChange={e => setShipper({ ...shipper, max_shipment_value: parseFloat(e.target.value) })} data-testid="ship-max-value" />
                </Field>
              </div>
              <Field label="Primary product categories" required>
                <div className="flex flex-wrap gap-2">
                  {cfg.product_categories.map(c => {
                    const on = shipper.primary_product_categories.includes(c);
                    return (
                      <button key={c} type="button" onClick={() => setShipper({ ...shipper, primary_product_categories: on ? shipper.primary_product_categories.filter(x => x !== c) : [...shipper.primary_product_categories, c] })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border ${on ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-200 dark:border-slate-700'}`}
                        data-testid={`ship-cat-${c}`}>
                        {c.replaceAll('_', ' ')}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm" data-testid="ship-intl">
                <input type="checkbox" checked={shipper.ships_internationally} onChange={e => setShipper({ ...shipper, ships_internationally: e.target.checked })} />
                Ships internationally
              </label>
              <label className="flex items-center gap-2 text-sm" data-testid="ship-restricted">
                <input type="checkbox" checked={shipper.ships_restricted_items} onChange={e => setShipper({ ...shipper, ships_restricted_items: e.target.checked })} />
                Ships restricted or regulated items
              </label>
              {shipper.ships_restricted_items && (
                <Field label="Which restricted categories?">
                  <div className="flex flex-wrap gap-2">
                    {cfg.restricted_categories.map(c => {
                      const on = (shipper.restricted_item_categories || []).includes(c);
                      return (
                        <button key={c} type="button" onClick={() => setShipper({ ...shipper, restricted_item_categories: on ? shipper.restricted_item_categories.filter(x => x !== c) : [...(shipper.restricted_item_categories || []), c] })}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border ${on ? 'bg-amber-500 text-white border-amber-500' : 'border-slate-200 dark:border-slate-700'}`}
                          data-testid={`ship-restcat-${c}`}>
                          {c.replaceAll('_', ' ')}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="ship-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => { const r = await post('shipper-profile', shipper); if (r) next(); }} disabled={saving} data-testid="ship-save">
                  {saving ? 'Saving…' : 'Save & continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 9 && (
            <div className={cardCls + " space-y-4"} data-testid="step-fraud">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                We run a multi-signal fraud scan across email, IP reputation, device fingerprint and geo — all under the <b>SelleSend Protect</b> layer.
                No merchant setup required.
              </p>
              <Button onClick={async () => {
                const r = await post('fraud-scan', { user_email: '', device_fingerprint: navigator.userAgent });
                if (r) setFraudResult(r);
              }} disabled={saving} data-testid="fraud-run">
                {saving ? 'Scanning…' : 'Run SelleSend Protect scan'}
              </Button>
              {fraudResult && (
                <div className="space-y-3" data-testid="fraud-result">
                  <div className={`p-4 rounded-xl border-2 ${
                    fraudResult.verdict === 'clear' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' :
                    fraudResult.verdict === 'review' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' :
                    'border-rose-500 bg-rose-50 dark:bg-rose-900/20'
                  }`}>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Verdict</p>
                    <p className="text-2xl font-semibold">{fraudResult.verdict.toUpperCase()} · risk {fraudResult.risk_score}</p>
                    {fraudResult.signals?.length > 0 && <p className="text-xs mt-1">Signals: {fraudResult.signals.join(', ')}</p>}
                  </div>
                </div>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="fraud-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={next} disabled={!fraudResult} data-testid="fraud-continue">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {step === 10 && (
            <div className={cardCls + " space-y-5"} data-testid="step-billing">
              {/* The merchant used to type a markup here that set nothing: the margin is SelleSend's and the admin sets it (Mike, 12-oct). */}
              <p className="text-sm text-slate-600 dark:text-slate-400">Choose your protection tiers and billing method. Payment is due on demand — wallet, credit card, or certified funds. No terms.</p>
              <Field label="Protection tiers">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  {['shipping', 'payment', 'alternative', 'return'].map(p => (
                    <div key={p}>
                      <p className="text-xs uppercase text-slate-500 mb-1">{p}</p>
                      <select className={inputCls} value={billing[`${p}_protection_tier`]} onChange={e => setBilling({ ...billing, [`${p}_protection_tier`]: e.target.value })} data-testid={`bill-tier-${p}`}>
                        {['basic', 'standard', 'premium', 'business', 'enterprise'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </Field>
              <Field label="Billing method" required>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {['wallet', 'credit_card', 'certified_funds'].map(m => (
                    <button key={m} type="button" onClick={() => setBilling({ ...billing, billing_method: m })}
                      className={`p-3 rounded-xl border-2 text-sm font-medium ${billing.billing_method === m ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                      data-testid={`bill-method-${m}`}>
                      {m.replaceAll('_', ' ')}
                    </button>
                  ))}
                </div>
              </Field>
              <label className="flex items-start gap-2 text-sm" data-testid="bill-ack">
                <input type="checkbox" className="mt-1" checked={billing.acknowledge_billing_terms} onChange={e => setBilling({ ...billing, acknowledge_billing_terms: e.target.checked })} />
                <span>I agree to due-on-demand billing terms. Service and protection are void if invoices are unpaid.</span>
              </label>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="bill-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => {
                  // Save into the legacy endpoints so the protection engine picks it up. Step 2 was the
                  // merchant's own markup, which never set the price; SelleSend's margin is set by the admin.
                  await fetch(`${API}/api/onboarding/step/1`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ company_name: company.legal_name || 'SelleSend Member', business_type: 'b2c', monthly_volume: shipper.monthly_shipment_volume || '1-50', primary_marketplace: 'custom' }) });
                  await fetch(`${API}/api/onboarding/step/3`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ shipping_protection_tier: billing.shipping_protection_tier, payment_protection_tier: billing.payment_protection_tier, alternative_protection_tier: billing.alternative_protection_tier, return_protection_tier: billing.return_protection_tier }) });
                  await fetch(`${API}/api/onboarding/step/4`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ billing_method: billing.billing_method, acknowledge_billing_terms: billing.acknowledge_billing_terms }) });
                  next();
                }} disabled={saving || !billing.acknowledge_billing_terms} data-testid="bill-save">Save & continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {step === 11 && (
            <div className={cardCls + " space-y-4"} data-testid="step-review">
              <p className="text-sm text-slate-600 dark:text-slate-400">Final step — read and accept each legal document. Your acceptance is logged with document version, timestamp, IP, and a SHA-256 evidence hash for FinCEN/SOC-2 audit.</p>
              {[
                ['accepted_terms_of_service', 'Terms of Service', '/dashboard/legal/terms-of-service'],
                ['accepted_privacy_policy', 'Privacy Policy', '/dashboard/legal/privacy-policy'],
                ['accepted_aml_policy', 'AML & Compliance Policy', '/dashboard/legal/aml-compliance'],
                ['accepted_shipping_terms', 'Shipping Terms', '/dashboard/legal/shipping-terms'],
                ['accepted_prohibited_items', 'Restricted & Prohibited Products', '/dashboard/legal/restricted-prohibited-products'],
                ['accepted_regulatory_disclosures', 'Regulatory Disclosures', '/dashboard/legal/regulatory-disclosures'],
                ['accepted_non_custodial', 'Non-Custodial Disclosure', '/dashboard/legal/non-custodial-disclosure'],
              ].map(([key, label, href]) => (
                <label key={key} className="flex items-start gap-2 text-sm p-3 rounded-lg border border-slate-200 dark:border-slate-700" data-testid={`review-${key}`}>
                  <input type="checkbox" className="mt-1" checked={review[key] || false} onChange={e => setReview({ ...review, [key]: e.target.checked })} />
                  <span>I have read and agree to the <a className="text-emerald-600 underline" href={href} target="_blank" rel="noreferrer">{label}</a>.</span>
                </label>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Signer full name" required>
                  <input className={inputCls} value={review.signer_full_name || ''} onChange={e => setReview({ ...review, signer_full_name: e.target.value })} data-testid="review-signer-name" />
                </Field>
                <Field label="Signer title" required>
                  <input className={inputCls} value={review.signer_title || ''} onChange={e => setReview({ ...review, signer_title: e.target.value })} data-testid="review-signer-title" />
                </Field>
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={back} data-testid="review-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                <Button onClick={async () => {
                  const r = await post('review', review);
                  if (r) {
                    setBanner({ type: 'success', text: r.message });
                    setTimeout(() => navigate('/dashboard'), 1500);
                  }
                }} disabled={saving} data-testid="review-submit">
                  {saving ? 'Signing…' : 'Sign & complete verification'} <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
